/**
 * Persists which Foundations baseline the user last opened (per workspace).
 * Used on the Brand Baseline dashboard so returning users can resume quickly.
 */

const STORAGE_KEY = 'cz_foundations_recent_v1'

export type FoundationsRecentContext = {
    runId: string
    runName: string
    subjectName?: string
    anchorType?: string
    stepId: string
    stepLabel: string
    pathname: string
    visitedAt: string
}

type StoredByWorkspace = Record<string, FoundationsRecentContext>

const STEP_LABELS: Record<string, string> = {
    overview: 'Overview',
    goal: 'Goal',
    target: 'Audience',
    tfd: 'Mindset',
    alo: 'Brand Truth',
    'foundational-symbols': 'Brand Voice',
    'message-strategy': 'Claims',
    'value-props': 'Value Props',
    'communication-briefs': 'Briefs',
}

/** Map current foundations route to a human step label. */
export function foundationsStepFromPath(pathname: string, runId: string): { stepId: string; stepLabel: string } {
    if (pathname.includes('/communication-briefs')) {
        return { stepId: 'communication-briefs', stepLabel: STEP_LABELS['communication-briefs'] }
    }
    if (pathname.endsWith('/value-props')) {
        return { stepId: 'value-props', stepLabel: STEP_LABELS['value-props'] }
    }
    if (pathname.endsWith('/message-strategy')) {
        return { stepId: 'message-strategy', stepLabel: STEP_LABELS['message-strategy'] }
    }
    if (pathname.endsWith('/foundational-symbols')) {
        return { stepId: 'foundational-symbols', stepLabel: STEP_LABELS['foundational-symbols'] }
    }
    if (pathname.endsWith('/alo')) {
        return { stepId: 'alo', stepLabel: STEP_LABELS.alo }
    }
    if (pathname.endsWith('/tfd')) {
        return { stepId: 'tfd', stepLabel: STEP_LABELS.tfd }
    }
    if (pathname.endsWith('/target')) {
        return { stepId: 'target', stepLabel: STEP_LABELS.target }
    }
    if (pathname.endsWith('/goal')) {
        return { stepId: 'goal', stepLabel: STEP_LABELS.goal }
    }
    const base = `/dashboard/brand-strategy/foundations/${runId}`
    if (pathname === base || pathname === `${base}/`) {
        return { stepId: 'overview', stepLabel: STEP_LABELS.overview }
    }
    return { stepId: 'overview', stepLabel: STEP_LABELS.overview }
}

function readAll(): StoredByWorkspace {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return {}
        const parsed = JSON.parse(raw) as StoredByWorkspace
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
        return {}
    }
}

function writeAll(data: StoredByWorkspace) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch {
        /* quota / private mode */
    }
}

export function getFoundationsRecent(workspaceId: string | undefined): FoundationsRecentContext | null {
    if (!workspaceId) return null
    const entry = readAll()[workspaceId]
    if (!entry?.runId || !entry.pathname) return null
    return entry
}

export function setFoundationsRecent(
    workspaceId: string | undefined,
    ctx: Omit<FoundationsRecentContext, 'visitedAt'> & { visitedAt?: string }
) {
    if (!workspaceId || !ctx.runId) return
    const all = readAll()
    all[workspaceId] = {
        ...ctx,
        visitedAt: ctx.visitedAt ?? new Date().toISOString(),
    }
    writeAll(all)
}

export function clearFoundationsRecent(workspaceId: string | undefined, runId?: string) {
    if (!workspaceId) return
    const all = readAll()
    const current = all[workspaceId]
    if (!current) return
    if (runId && current.runId !== runId) return
    delete all[workspaceId]
    writeAll(all)
}
